import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';

import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IEnvironmentComponentProps } from '../../../../models/AppState';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { copyToClipboard } from '../../../../models/UiUtils';
import { IFileAttachment } from '../../../../view/models/Api';
import defaultImage from '../../../assets/default_event_image.svg';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { FabContext } from '../../../components/FabContext';
import { ImageFileChooserModal } from '../../../components/ImageFileChooserModal';
import { MediaChooserModal } from '../../../components/MediaChooser';
import { IModalProps } from '../../../components/Modal';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../components/svgs/icons/TabSettingsIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../components/Tabs';
import { useClonableSurvey } from '../../../hooks/useClonableSurvey';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { AdditionalQuestions } from '../shared/components/AdditionalQuestions';
import { ReorderQuestionsButton } from '../shared/components/ReorderQuestionsButton';
import { ReorderQuestionsModalComponent } from '../shared/components/ReorderQuestionsModal/ReorderQuestionsModal';
import { SectionTitle } from '../shared/components/FormFieldWithOptions/presentation';
import { SurveyActions } from '../shared/components/SurveyActions';
import { SurveyHeader } from '../shared/components/SurveyHeader';
import { SurveySettings } from '../shared/components/SurveySettings';
import { UnsavedChangesWarning } from '../shared/components/UnsavedChangesWarning';
import { useUnsavedChanges } from '../shared/hooks/useUnsavedChanges';
import { hasCustomFormChanges } from '../shared/utils/customFormComparison';
import { AttendeesSettings, EventDetailsSettings, PaymentsSettings, PreviewEventSurveyForm } from './components';
import {
	useEventRegistrationFormInitialization,
	useEventRegistrationFormValidation,
	useEventRegistrationSurveyMutations,
} from './hooks';
import { styleSheet } from './styles';
import { EventRegistrationFormData, isValidEventDate, TabValues, validationSchema } from './utils';

const { useCallback } = React;

const FALL_BACK_IMAGE = defaultImage;

/**
 * Processes event tickets to set the cheapest ticket as default and ensure attendeeCountPerItem is 1
 * If a default ticket is already set from the backend, it will be preserved
 * Also deduplicates tickets by ID to prevent backend duplication issues
 * @param tickets - Array of event tickets from form data
 * @returns Processed tickets with isDefault set correctly and duplicates removed
 */
const processEventTickets = (tickets: Api.IEventTicket[]): Api.IEventTicket[] => {
	if (tickets.length === 0) return [];

	// Deduplicate tickets by ID (defensive against backend creating duplicates)
	const uniqueTicketsMap = new Map<string, Api.IEventTicket>();
	tickets.forEach(ticket => {
		if (ticket.id && !uniqueTicketsMap.has(ticket.id)) {
			uniqueTicketsMap.set(ticket.id, ticket);
		}
	});
	const uniqueTickets = Array.from(uniqueTicketsMap.values());

	// Check if any ticket already has isDefault set to true
	const hasExistingDefault = uniqueTickets.some(ticket => ticket.isDefault === true);

	if (hasExistingDefault) {
		// Preserve existing default, just ensure attendeeCountPerItem is 1
		return uniqueTickets.map(ticket => ({
			...ticket,
			attendeeCountPerItem: 1,
		}));
	}

	// No existing default, find the cheapest ticket (lowest unitPrice)
	const cheapestTicket = uniqueTickets.reduce(
		(min, ticket) => ((ticket.unitPrice || 0) < (min.unitPrice || 0) ? ticket : min),
		uniqueTickets[0]
	);

	// Map tickets: set cheapest to isDefault: true, others to false, ensure attendeeCountPerItem is 1
	return uniqueTickets.map(ticket => ({
		...ticket,
		isDefault: ticket.id === cheapestTicket.id,
		attendeeCountPerItem: 1,
	}));
};

interface IProps extends IEnvironmentComponentProps, IImpersonationContextComponentProps {
	className?: string;
}

interface IEditSurveyComponent {
	EventDetailsSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
	AttendeesSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
	PaymentsSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
}

export interface IState {
	enableRequestAttendeesToRSVP: boolean;
	fileInputModalOpen: boolean;
	now: Date;
	selectedImage: IFileAttachment | null;
	customFormIsDisabled: boolean;
	selectedTab: TabValues;
	showEditSurveyName: boolean;
	showReorderModal: boolean;
}

const EventRegistrationSurveyBase: React.FC<IProps> & Partial<IEditSurveyComponent> = ({
	className,
	impersonationContext,
}) => {
	const userSession = useUserSession();
	const [isQRModalOpen, setIsQRModalOpen] = React.useState(false);

	const [state, setState] = React.useState<IState>({
		enableRequestAttendeesToRSVP: false,
		fileInputModalOpen: false,
		now: new Date(),
		selectedImage: null,
		customFormIsDisabled: false,
		selectedTab: TabValues.Settings,
		showEditSurveyName: false,
		showReorderModal: false,
	});

	const account = impersonationContext ? impersonationContext.account : userSession.account;

	const history = useHistory();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EventRegistrationSurvey');
	const params = useParams<{ id?: string }>();
	const eventId = params?.id;

	const { surveyData, isClone, isEdit } = useClonableSurvey<Api.IEventRegistrationSurvey>(
		eventId,
		impersonationContext,
		'eventRegistration'
	);
	const eventData = surveyData;
	const pastEvent = React.useMemo(() => {
		if (isClone) {
			return false;
		}

		const dueDateString = eventData?.eventInformation?.startTime;
		return dueDateString ? moment(dueDateString).isBefore(new Date(), 'day') : false;
	}, [eventData, isClone]);

	const methods = useForm({
		defaultValues: {
			details: '',
			startDate: undefined,
			startTime: undefined,
			endDate: undefined,
			endTime: undefined,
			eventName: '',
			guestLimit: 1,
			location: '',
			maximumCapacity: 1,
			registrationDeadline: null,
			requireDeadline: false,
			requireMaximumCapacity: false,
			setGuestLimit: false,
			requirePhoneNumber: false,
			requireGuestNames: false,
			enableRequestAttendeesToRSVP: false,
			customForm: {
				name: '',
				fields: [] as Api.IFormField<string>[],
			},
			isPaidEvent: false,
			payments: {
				cost: 0,
			},
			eventTickets: [] as Api.IEventTicket[],
			timezone: userSession.user.userPreferences?.timeZone || moment.tz.guess(),
			notificationSettings: {
				isEnabled: false,
				additionalRecipients: [] as string[],
			},
			tagGenerationSettings: {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			},
		},
		resolver: yupResolver(validationSchema),
	});
	const { handleSubmit, formState, getValues, watch, setValue, reset, clearErrors } = methods;
	const formValues = watch();
	const hasImages = Boolean(state.selectedImage);

	const {
		publishableApiKeys,
		eventRegistrationMutation,
		updateSurveyEventMutation,
		imageAttachmentMutation,
		reorderSurveyFieldsMutation,
	} = useEventRegistrationSurveyMutations({
		impersonationContext,
		history,
		errorMessages,
		logApiError,
		getValues,
		reset,
		clearErrors,
		setState,
		onReorderSuccess: reorderedFields => {
			// Get current form fields to preserve their complete data (including options)
			const currentFields = getValues().customForm?.fields || [];

			// Create a map of current fields by ID for quick lookup
			const currentFieldsMap = new Map(currentFields.map(field => [field.id, field]));

			// Reorder the complete field objects based on the server response order
			const reorderedCompleteFields = reorderedFields
				.map(serverField => currentFieldsMap.get(serverField.id))
				.filter(Boolean) as Api.IFormField<string>[];

			setValue('customForm.fields', reorderedCompleteFields);
		},
	});

	// Use the new hooks for form initialization and validation
	useEventRegistrationFormInitialization({
		eventData,
		isClone,
		setValue,
		setState,
	});

	useEventRegistrationFormValidation({
		formState: formState as FormState<EventRegistrationFormData>,
		setState,
	});

	/**
	 * Pixabay selection setup
	 */
	const onFreeImageChooserRequestCloseRef = React.useRef<(img?: IFileAttachment, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});
	const onFreeImageChooserRequestClose = React.useCallback((imageFile?: IFileAttachment, cancel?: boolean) => {
		setFreeImageModalProps(prevState => {
			return {
				...prevState,
				isOpen: false,
			};
		});
		if (cancel) {
			return;
		}
		setState(prevState => ({
			...prevState,
			selectedImage: imageFile,
		}));
	}, []);
	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;
	/**
	 * End Pixabay selection setup
	 */

	const handleSave = () => {
		if (pastEvent) {
			toast.error('Cannot edit a past event');
			return;
		}

		const formData = getValues();

		let endTimeMoment = null;
		if (!isValidEventDate(formData)) {
			return;
		}
		if (formData.endTime && formData.endDate) {
			endTimeMoment = moment.tz(formData.endDate + ' ' + formData.endTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone);
		} else if (formData.endTime) {
			endTimeMoment = moment.tz(formData.startDate + ' ' + formData.endTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone);
		}
		const endTime = endTimeMoment ? endTimeMoment.toDate() : null;
		const startTime = moment
			.tz(formData.startDate + ' ' + formData.startTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone)
			.toDate();
		const eventRegistration: Api.IEventRegistrationSurvey = {
			_type: 'EventRegistrationSurvey',
			attendeeOptions: {
				enabled: formData.enableRequestAttendeesToRSVP,
				// For paid events with tiered tickets, guestLimit should not be used (set to null)
				// For free events or legacy paid events, use the configured guestLimit or 0
				guestLimit:
					formData.isPaidEvent && formData.eventTickets && formData.eventTickets.length > 0
						? null
						: formData.enableRequestAttendeesToRSVP && formData.setGuestLimit
							? formData.guestLimit
							: 0,
				maximumCapacity:
					formData.enableRequestAttendeesToRSVP && !formData.isPaidEvent && formData.maximumCapacity > 1
						? formData.maximumCapacity
						: null,
				registrationDeadline: formData.enableRequestAttendeesToRSVP
					? formData.registrationDeadline
						? new Date(formData.registrationDeadline)
						: null
					: null,
				requirePhoneNumber: formData.enableRequestAttendeesToRSVP ? formData.requirePhoneNumber : false,
				requireGuestNames: formData.enableRequestAttendeesToRSVP ? formData.requireGuestNames : false,
				// Only send costPerAttendee for legacy events (no tickets), otherwise backend creates duplicate tickets
				costPerAttendee:
					formData.isPaidEvent && (!formData.eventTickets || formData.eventTickets.length === 0)
						? formData.payments.cost * 100
						: null,
				eventTickets: processEventTickets(formData.eventTickets || []),
			},
			eventInformation: {
				details: formData.details,
				endTime,
				location: formData.location,
				startTime,
				timeZone: formData.timezone,
			},
			name: formData.eventName,
			customFormIsDisabled: state.customFormIsDisabled,
			isPaidEvent: formData.isPaidEvent,
			notificationSettings: {
				_type: 'SurveyNotificationSettings',
				isEnabled: formData.notificationSettings.isEnabled,
				additionalRecipients: formData.notificationSettings.additionalRecipients.filter(
					recipient => recipient && recipient.trim() !== ''
				),
			},
			customForm: {
				name: isClone ? eventData?.name : formData.customForm.name,
				fields: formData.customForm.fields,
			},
			tagGenerationSettings: {
				tagOnCompletion: formData.tagGenerationSettings.tagOnCompletion,
				completionTag: formData.tagGenerationSettings.completionTag,
				customFormFieldTagSettings: formData.tagGenerationSettings.customFormFieldTagSettings,
			},
		};
		if (state.selectedImage) {
			eventRegistration.eventInformation.image = state.selectedImage;
		}

		const needsOptions = formData.customForm.fields?.some((field: Api.IFormField<string>) => {
			return (
				(field.fieldType === Api.FormFieldType.Option || field.fieldType === Api.FormFieldType.MultipleOptions) &&
				field.options?.length > 0 &&
				field.options.find(option => option.label.trim().length === 0)
			);
		});
		const needsFieldsLabel = formData.customForm.fields.some(
			field => field.label.trim().length === 0 && field.fieldType !== Api.FormFieldType.String
		);

		if (needsFieldsLabel) {
			errorMessages.push({
				messages: ['Please add a label to all fields before saving.'],
			});
			return;
		}

		if (needsOptions) {
			errorMessages.push({
				messages: ['Please add options to all fields that are of type "Checkbox" or "Dropdown" before saving.'],
			});
			toast.error('Event not saved');
			return;
		}

		if (isEdit && !isClone) {
			// When editing existing survey (not cloning), merge with original data
			const payload = {
				...eventData,
				...eventRegistration,
			};
			updateSurveyEventMutation.mutate({ eventRegistration: payload, id: eventId });
			return;
		}
		eventRegistrationMutation.mutate({
			eventRegistration,
		});
	};

	// Track if customForm fields have meaningful changes by comparing specific properties
	const customFormFieldsHaveChanges = React.useMemo(
		() => hasCustomFormChanges(eventData?.customForm?.fields, formValues.customForm?.fields),
		[eventData?.customForm?.fields, formValues.customForm?.fields]
	);

	const hasUnsavedChanges = useUnsavedChanges({
		surveyType: 'event',
		isEdit,
		isClone,
		formState,
		additionalConditions: [
			formValues.enableRequestAttendeesToRSVP !== eventData?.attendeeOptions?.enabled,
			state.selectedImage?.url !== eventData?.eventInformation?.image?.url,
			customFormFieldsHaveChanges,
		],
	});

	const onClickMedia = () => {
		setState(prevState => ({
			...prevState,
			fileInputModalOpen: true,
		}));
	};

	const onRequestfileInputClose = (val?: File | null, cancel?: boolean) => {
		if (cancel) {
			setState(prevState => ({
				...prevState,
				fileInputModalOpen: false,
			}));
			return;
		}
		imageAttachmentMutation.mutate({ files: [val] });
		setState(prevState => ({
			...prevState,
			fileInputModalOpen: false,
		}));
	};

	const onClickFreeImage = () => {
		setFreeImageModalProps(value => {
			return {
				...value,
				isOpen: true,
			};
		});
	};

	const onCopyLinkClicked = React.useCallback(() => {
		if (copyToClipboard(eventData?.anonymousLink)) {
			toast.success('Link copied to clipboard');
			return;
		}

		toast.error('Error copying link to clipboard');
	}, [eventData?.anonymousLink]);

	const setSelectedTab = (value: TabValues) => {
		setState(prevState => ({
			...prevState,
			selectedTab: value,
		}));
	};

	const onToggleCheckChanged = () => {
		setState(prevState => ({
			...prevState,
			enableRequestAttendeesToRSVP: !prevState.enableRequestAttendeesToRSVP,
		}));
	};

	// Computed values for preview section
	const formattedAmount = React.useMemo(() => {
		// For tiered tickets, show the cheapest ticket price
		if (formValues?.eventTickets && formValues.eventTickets.length > 0) {
			const cheapestTicket = formValues.eventTickets.reduce(
				(min, ticket) => ((ticket.unitPrice || 0) < (min.unitPrice || 0) ? ticket : min),
				formValues.eventTickets[0]
			);
			if (cheapestTicket?.unitPrice) {
				const dollarAmount = new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
				}).format(cheapestTicket.unitPrice / 100);
				return dollarAmount;
			}
		}
		// Legacy: use payments.cost
		if (formValues?.payments?.cost) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(formValues?.payments?.cost);
			return dollarAmount;
		}
		return '$0.00';
	}, [formValues?.payments?.cost, formValues?.eventTickets]);

	const isEventDateValid = React.useMemo(() => {
		return isValidEventDate(formValues);
	}, [formValues]);

	const eventDateTimeText = React.useMemo(() => {
		if (!isEventDateValid) return '';

		if (formValues.endDate && formValues.endTime && formValues.endDate !== formValues.startDate) {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} - ${moment.tz(formValues.endDate + ' ' + formValues.endTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		} else if (formValues.endTime) {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} - ${moment(formValues.endTime, 'HH:mm:ss').format('hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		} else {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		}
	}, [formValues, isEventDateValid]);

	const settingsErrors = Object.keys(formState.errors).some(x =>
		['details', 'startDate', 'startTime', 'eventName', 'location', 'payments', 'notificationSettings'].includes(x)
	);
	const customFormErrors = Object.keys(formState.errors).some(x => x.startsWith('customForm'));

	const onQRCodeClick = () => {
		setIsQRModalOpen(true);
	};

	const handleQRModalClose = () => {
		setIsQRModalOpen(false);
	};

	const handleReorderQuestions = () => {
		setState(prevState => ({ ...prevState, showReorderModal: true }));
	};

	const handleReorderModalClose = useCallback(
		(result?: { reorderedQuestions?: any }, cancel?: boolean) => {
			setState(prevState => ({ ...prevState, showReorderModal: false }));
			if (!cancel && result?.reorderedQuestions) {
				reorderSurveyFieldsMutation.mutate({
					surveyId: eventData.id,
					fieldIds: result.reorderedQuestions.map((field: Api.IFormField<string>) => field.id),
				});
			}
		},
		[reorderSurveyFieldsMutation, eventData]
	);

	const questions = formValues?.customForm?.fields || [];
	const hasMultipleQuestions = questions.length > 1;

	const breadcrumbPageTitle = useCallback(() => {
		if (isClone) {
			return 'Clone Event';
		}
		return params.id ? (eventData?.name ?? 'Edit Event') : 'New Event';
	}, [params.id, eventData?.name, isClone]);
	return (
		<Page styles={[className ?? '', styleSheet.page]}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/surveys/event'>Surveys</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{breadcrumbPageTitle()}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
			/>
			<FabContext appearance={{ hidden: true }} />
			{pastEvent ? (
				<h2 className={css(styleSheet.notEditableNotice)}>This event has passed and its no longer editable</h2>
			) : null}
			<FormProvider {...methods}>
				<form className='form-container' onSubmit={handleSubmit(handleSave)}>
					<fieldset disabled={pastEvent} className={css(styleSheet.fieldset, styleSheet.formStyles)}>
						<div className={css(styleSheet.header)}>
							<SurveyHeader
								surveyName={formValues.eventName || 'Untitled Event'}
								showEditName={state.showEditSurveyName}
								onEditToggle={() => setState(prev => ({ ...prev, showEditSurveyName: !prev.showEditSurveyName }))}
								onNameChange={name => setValue('eventName', name)}
								validationError={formState.errors?.eventName?.message}
								isEdit={isEdit}
								fieldName='eventName'
							/>
							<SurveyActions
								onCopyLink={onCopyLinkClicked}
								isLoading={eventRegistrationMutation?.isPending || updateSurveyEventMutation?.isPending}
								isSaveDisabled={pastEvent || state.showEditSurveyName}
								showCopyLink={!!eventData?.id}
								surveyLink={eventData?.anonymousLink}
								onQRCodeClick={onQRCodeClick}
							/>
						</div>
						<UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
						<TabRoot
							className={`tab-view ${css(styleSheet.tabViewContainer)}`}
							value={state.selectedTab}
							onValueChange={tValue => setSelectedTab(tValue as TabValues)}
						>
							<TabList styles={[styleSheet.tabList]}>
								<IconTabTrigger
									value={TabValues.Settings}
									icon={<TabSettingsIcon fillColor='currentColor' />}
									styles={settingsErrors ? [styleSheet.tabTextError] : []}
								>
									Settings
								</IconTabTrigger>
								<IconTabTrigger
									value={TabValues.Questions}
									styles={customFormErrors ? [styleSheet.tabTextError] : []}
									icon={<ListOfQuestionsIcon fillColor='currentColor' />}
								>
									Questions
								</IconTabTrigger>
								{eventData?.anonymousLink ? (
									<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
										Preview
									</IconTabTrigger>
								) : null}
							</TabList>
							<TabContent value={TabValues.Settings} className={css(styleSheet.tabContent)}>
								<div className={css(styleSheet.introText)}>
									<h3>Event Settings</h3>
									<p className={css(styleSheet.eventLabel)}>
										Once attendees RSVP, you can send personalized follow-up emails based on their response, whether
										they&apos;re attending or not. If you send the invitation through Levitate, you&apos;ll also be able
										to see who hasn&apos;t responded yet.
									</p>
								</div>
								<SurveySettings
									styles={[styleSheet.settingsContainer]}
									tabListStyles={[styleSheet.settingsTabList]}
									tabs={[
										{
											title: 'Event Details',
											key: 'event-details',
											content: (
												<EventDetailsSettings
													state={{
														enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
														now: state.now,
														selectedImage: state.selectedImage,
													}}
													eventData={eventData}
													account={account}
													publishableApiKeys={publishableApiKeys}
													onToggleCheckChanged={onToggleCheckChanged}
													onClickMedia={onClickMedia}
													onClickFreeImage={onClickFreeImage}
													isClone={isClone}
													setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
												/>
											),
										},
										{
											title: 'Attendees',
											key: 'attendees',
											content: (
												<AttendeesSettings
													state={{
														enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
														now: state.now,
														selectedImage: state.selectedImage,
													}}
													eventData={eventData}
													account={account}
													publishableApiKeys={publishableApiKeys}
													onToggleCheckChanged={onToggleCheckChanged}
													onClickMedia={onClickMedia}
													onClickFreeImage={onClickFreeImage}
													isClone={isClone}
													setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
												/>
											),
										},
										{
											title:
												account?.features?.stripePayment?.enabled && Boolean(publishableApiKeys)
													? 'Payments & Notifications'
													: 'Notifications',
											key: 'payments-notifications',
											content: (
												<PaymentsSettings
													state={{
														enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
														now: state.now,
														selectedImage: state.selectedImage,
													}}
													eventData={eventData}
													account={account}
													publishableApiKeys={publishableApiKeys}
													onToggleCheckChanged={onToggleCheckChanged}
													onClickMedia={onClickMedia}
													onClickFreeImage={onClickFreeImage}
													isClone={isClone}
													setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
												/>
											),
										},
									]}
								/>
							</TabContent>
							<TabContent value={TabValues.Questions} className={css(styleSheet.tabContent)}>
								<div className={css(styleSheet.formContainer)}>
									<div className={css(styleSheet.bodyBackground)} />
									<section className={css(styleSheet.sectionHeader)}>
										<SectionTitle
											title='Additional Questions'
											blurb='Once the survey is saved, the question type cannot be changed.'
										/>
									</section>
									<ReorderQuestionsButton
										onReorderQuestions={handleReorderQuestions}
										hasMultipleQuestions={hasMultipleQuestions}
										isLoading={eventRegistrationMutation.isPending || updateSurveyEventMutation.isPending}
									/>
									<div className={css(styleSheet.additionalQuestionsContainer)}>
										<AdditionalQuestions isEdit={isEdit} isClone={isClone} />
									</div>
								</div>
							</TabContent>
							{eventData?.anonymousLink ? (
								<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent)}>
									<div className={css(styleSheet.bodyBackground)} />
									<div className={css(styleSheet.surveyContainer)}>
										<div className={css(styleSheet.eventDetailsContainer)}>
											<figure className={css(styleSheet.previewImageContainer)}>
												<img
													className={css(styleSheet.previewImage)}
													src={hasImages ? state?.selectedImage?.url : FALL_BACK_IMAGE}
													alt={`event graphic for ${formValues?.eventName}`}
												/>
											</figure>
											<section className={css(styleSheet.details)}>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>When</h4>
													<p>{eventDateTimeText}</p>
												</div>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>Where</h4>
													<p className={css(styleSheet.locationAreaWrap)}>{formValues?.location}</p>
												</div>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>Details</h4>
													<pre className={css(styleSheet.detailsAreaWrap)}>{formValues?.details}</pre>
												</div>
												{/* Only show Cost section for legacy events (costPerAttendee), not tiered tickets */}
												{formValues?.isPaidEvent &&
												formValues?.payments?.cost &&
												(!formValues?.eventTickets || formValues.eventTickets.length === 0) ? (
													<div>
														<h4 className={css(styleSheet.eventDetailsTitle)}>Cost</h4>
														<p className={css(styleSheet.eventPrice)}>{formattedAmount}/ticket</p>
													</div>
												) : null}
											</section>
										</div>

										<PreviewEventSurveyForm
											survey={eventData}
											formValues={formValues}
											enableRequestAttendeesToRSVP={state.enableRequestAttendeesToRSVP}
										/>
									</div>
								</TabContent>
							) : null}
						</TabRoot>
					</fieldset>
				</form>
			</FormProvider>
			<ImageFileChooserModal
				modalProps={{
					isOpen: state.fileInputModalOpen,
					onRequestClose: onRequestfileInputClose,
				}}
			/>
			<MediaChooserModal
				modalProps={freeImageModalProps}
				useBasicSearch={true}
				optionalImageName='External Image'
				imageOnly={true}
				styles={[styleSheet.mediaModalStyle]}
				onSave={onFreeImageChooserRequestClose}
			/>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>

			<ReorderQuestionsModalComponent
				modalProps={{
					isOpen: state.showReorderModal,
					onRequestClose: handleReorderModalClose,
				}}
				questions={questions}
				surveyId={eventId || ''}
				impersonationContext={impersonationContext}
			/>
			<QRCodeModal
				modalProps={{
					isOpen: isQRModalOpen,
					onRequestClose: handleQRModalClose,
				}}
				url={eventData?.anonymousLink || ''}
				title='QR Code'
				filename={`survey-qr-${eventData?.id || 'code'}`}
			/>
		</Page>
	);
};

export const EventRegistrationSurvey = inject(ImpersonationContextKey)(EventRegistrationSurveyBase);

EventRegistrationSurvey.EventDetailsSettings = EventDetailsSettings;
EventRegistrationSurvey.AttendeesSettings = AttendeesSettings;
EventRegistrationSurvey.PaymentsSettings = PaymentsSettings;
